// Kid onboarding — Step 2: Age (4/5/6/7). Drives diagnostic difficulty seed.
// Engine AgeBand covers "4.0-4.5" → "7.0+". Younger/older route to waitlist.

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/supabaseClient.js";
import { isDevBypass } from "@/lib/persistence";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount, useKids } from "@/lib/account";
import { kidStepProgress } from "@/lib/kidOnboarding";
import { useOnboardingDraft } from "@/lib/onboardingDraft";

export const Route = createFileRoute("/onboarding/kid/age")({
  head: () => ({ meta: [{ title: "PencilWise · Child's age" }] }),
  component: KidAge,
});

const AGES = [4, 5, 6, 7] as const;

type Waitlist = "under_4" | "over_7" | null;

// H-A4 shared step styles (H-0 tokens) — see onboarding.kid.name.tsx.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";
const INPUT =
  "w-full min-h-[56px] rounded-2xl border-2 border-border bg-card px-5 text-base font-semibold text-foreground shadow-soft placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none";

function KidAge() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  const kid = kids[kids.length - 1] ?? null;
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [waitlist, setWaitlist] = useState<Waitlist>(null);
  const [email, setEmail] = useState("");
  const [childAge, setChildAge] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const { current, total } = kidStepProgress("age");

  const { setDraft } = useOnboardingDraft();
  const submit = async (age: number) => {
    setPicked(age);
    setBusy(true);
    // Phase G3 Stage 4: persistence at handoff via the gate-ladder
    // bridge; per-step supabase writes skipped (mock no-op anyway).
    setDraft({ age });
    navigate({ to: "/onboarding/kid/pal", search: (prev) => prev });
  };

  const submitWaitlist = async () => {
    if (!email || !waitlist) return;
    setBusy(true);
    // De-mock (PR #15): real `waitlist` insert (was the storybook
    // `age_waitlist` mock). This is the one intentionally-anonymous
    // write — RLS `WITH CHECK (true)` — so it works for real
    // unauthenticated visitors. It is skipped under the dev/e2e bypass
    // so test runs never pollute the real table. `age_band`/`child_age`
    // have no `waitlist` columns (deferred to Phase H); the age band is
    // encoded into `source`.
    if (!isDevBypass()) {
      try {
        await supabase.from("waitlist").insert({
          email,
          source: `age_gate:${waitlist}`,
        });
      } catch {
        // best-effort — the UI confirms either way
      }
    }
    setSubmitted(true);
    setBusy(false);
  };

  if (waitlist) {
    const copy =
      waitlist === "under_4"
        ? "PencilWise is built for ages 4–7. Want to join the waitlist for our 3-and-under expansion?"
        : "PencilWise is built for ages 4–7. Want to join the waitlist for our 8–10 expansion?";
    return (
      <div className="relative min-h-screen bg-cream paper-grain">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
          <header className="pointer-events-none animate-slide-up">
            <p className={EYEBROW}>Waitlist</p>
            <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
              Not quite the right age yet.
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy}</p>
          </header>
          {submitted ? (
            <div className="mt-6 rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <p className="text-sm font-bold">Thanks — you're on the list.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                We'll email {email} the moment it's ready.
              </p>
              <button
                onClick={() => setWaitlist(null)}
                className="mt-4 inline-flex min-h-[44px] items-center text-sm font-bold text-terracotta-deep underline underline-offset-2"
              >
                ← Back to age picker
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <input
                type="number"
                min={1}
                max={20}
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
                placeholder="Child's age"
                className={INPUT}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={INPUT}
              />
              <button disabled={busy || !email} onClick={submitWaitlist} className={CTA}>
                {busy ? "Saving…" : "Join the waitlist"}
              </button>
              <button
                onClick={() => setWaitlist(null)}
                className="flex min-h-[44px] w-full items-center justify-center text-sm font-bold text-muted-foreground underline underline-offset-2"
              >
                ← Back
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>
            Step {current} of {total}
          </p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            How old is {kid?.name ?? "your child"}?
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            We use this to start the diagnostic at roughly the right level.
          </p>
        </header>
        <div className="mt-6 grid grid-cols-4 gap-3">
          {AGES.map((a) => (
            <button
              key={a}
              disabled={busy}
              onClick={() => submit(a)}
              className={`rounded-2xl border-2 px-2 py-6 font-serif text-3xl font-bold transition-all active:scale-[0.97] ${
                picked === a
                  ? "border-primary bg-primary text-primary-foreground shadow-press"
                  : "border-border bg-card text-foreground shadow-soft hover:border-primary"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={() => setWaitlist("under_4")}
            className="min-h-[44px] rounded-2xl border border-dashed border-ink/25 bg-transparent px-3 py-3 text-xs font-bold text-muted-foreground transition-colors hover:text-foreground"
          >
            Younger than 4
          </button>
          <button
            onClick={() => setWaitlist("over_7")}
            className="min-h-[44px] rounded-2xl border border-dashed border-ink/25 bg-transparent px-3 py-3 text-xs font-bold text-muted-foreground transition-colors hover:text-foreground"
          >
            Older than 7
          </button>
        </div>
      </div>
    </div>
  );
}
